import { LitElement, html, css, unsafeCSS } from '@hh/global.js'
import { utils } from '@hh/global.js'

const { isBase64 } = utils
class AdaptiveImage extends LitElement {
  static properties = {
    image: {
      type: String,
      converter: {
        fromAttribute: (value) => {
          const imageData = isBase64(value) ? atob(value) : value

          try {
            return JSON.parse(imageData)
          } catch (e) {
            return imageData
          }
        },
      },
    },
    mobileImage: {
      type: String,
      attribute: 'mobile-image',
      converter: {
        fromAttribute: (value) => {
          const imageData = isBase64(value) ? atob(value) : value

          try {
            return JSON.parse(imageData)
          } catch (e) {
            return imageData
          }
        },
      },
    },
    alt: { type: String },
    sizes: { type: String },
    loading: { type: String },
    objectFit: { type: String, attribute: 'object-fit' },
    aspectRatio: {
      type: Number,
      attribute: 'aspect-ratio',
      converter: {
        fromAttribute: (value) => parseFloat(value),
      },
    },
    mobileAspectRatio: {
      type: Number,
      attribute: 'mobile-aspect-ratio',
      converter: {
        fromAttribute: (value) => parseFloat(value),
      },
    },
    width: { type: Number },
    height: { type: Number },
    class: { type: String }
  }

  constructor() {
    super()
    // This list of asset widths to select from will be comprehensive for most use cases
    this.srcsetWidths = [493, 600, 713, 823, 1100, 1206, 1346, 1426, 1646, 1946]

    // Setting default values
    this.image = null
    this.mobileImage = null
    this.alt = ''
    this.sizes = '100vw'
    this.loading = 'lazy'
    this.objectFit = 'cover'
    this.aspectRatio = 1
    this.mobileAspectRatio = 1
    this.width = 1946
  }

  connectedCallback() {
    super.connectedCallback()

    this.style.setProperty('--hh-object-fit', this.objectFit)
    this.style.setProperty('--hh-aspect-ratio', this._aspectRatio)
    this.style.setProperty('--hh-mobile-aspect-ratio', this._mobileAspectRatio)
  }

  get _width() {
    return this.image?.width || this.width
  }

  get _height() {
    return this.image?.height || this.height
  }

  get _mobileWidth() {
    return this.mobileImage?.width || 750
  }

  get src() {
    return this.image.src || this.image
  }

  get mobileSrc() {
    return this.mobileImage.src || this.mobileImage
  }

  get _alt() {
    return this.alt || this.image?.alt
  }

  get _aspectRatio() {
    return this.aspectRatio || this.image?.aspect_ratio || 1
  }

  get _mobileAspectRatio() {
    return (
      this.mobileAspectRatio ||
      this.mobileImage?.aspect_ratio ||
      this._aspectRatio ||
      1
    )
  }

  updateImage(data) {
    Object.assign(this, data)
    this.render()
  }

  generateSrcset(src, imgWidth) {
    let srcset = ''

    for (const width of this.srcsetWidths) {
      if (imgWidth >= width) srcset += `${src}&width=${width} ${width}w,`
    }
    srcset += `${src} ${imgWidth}w`
    return srcset
  }

  render() {
    return html`
      <picture class="${this.class}__picture">
        ${this.mobileImage
          ? html`<source
                media="(min-width: 750px)"
                srcset="${this.generateSrcset(this.src, this._width)}"
                sizes="${this.sizes}"
                width="${this._width}"
                height="${this._height}"
              />
              <source
                media="(max-width: 749px)"
                srcset="
                  ${this.generateSrcset(this.mobileSrc, this._mobileWidth)}
                "
                sizes="${this.sizes}"
                width="${this._width}"
                height="${this._height}"
              />`
          : html`<source srcset="${this.generateSrcset(
              this.src,
              this._width
            )}" sizes="${this.sizes}"
                width="${this._width}"
                height="${this._height}"
            ></source>`}

        <img
          id="image-element"
          src="${this.src}&width=${this.srcsetWidths[0]}"
          alt="${this.alt}"
          loading="${this.loading}"
          width="${this._width}"
          height="${this._height}"
        />
      </picture>
    `
  }

  static styles = css`
    :host {
      display: block;
      height: auto;
      position: relative;
      width: 100%;
      aspect-ratio: var(--hh-mobile-aspect-ratio);
    }
    @media screen and (min-width: 750px) {
      :host {
        aspect-ratio: var(--hh-aspect-ratio);
      }
    }
    @supports not (aspect-ratio: ${unsafeCSS('var(--hh-aspect-ratio)')}) {
      :host::before {
        float: left;
        padding-top: calc(100% / ${unsafeCSS('var(--hh-aspect-ratio)')});
        content: '';
      }
      :host::after {
        display: block;
        clear: both;
        content: '';
      }
    }
    #image-element {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      object-fit: var(--hh-object-fit);
      -webkit-transform: translate3d(0, 0, 0);
    }
    @media screen and (max-width: 750px) {
      .short-banner__image__picture #image-element {
        height: auto;
      }
    }
  `
}

customElements.define('adaptive-image', AdaptiveImage)
